import { MapPin } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
/**
 * MiniMapPreview — tiny inline SVG map mockup for the
 * "Pick location on map" chip teaser. Pure SVG, ~3KB, no images.
 * Shows a stylized UAE-ish street map with 3 tutor pins.
 */
const MiniMapPreview = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';
  return (
    <div
      className={`mt-3 relative rounded-xl overflow-hidden border ${
        isDark ? 'border-white/10' : 'border-slate-200'
      }`}
      data-testid="mini-map-preview"
      aria-label={localize({ ar: 'معاينة الخريطة', en: 'Map preview' })}
    >
      <svg
        viewBox="0 0 240 96"
        className="block w-full h-20"
        preserveAspectRatio="xMidYMid slice"
        role="img"
      >
        {/* Background */}
        <rect width="240" height="96" fill={isDark ? '#0f172a' : '#eef2ff'} />

        {/* Road grid — lighter overlay */}
        <g stroke={isDark ? '#1e293b' : '#c7d2fe'} strokeWidth="1.2" opacity="0.7" fill="none">
          {/* Major roads */}
          <path d="M0 35 L240 35" />
          <path d="M0 70 L240 70" />
          <path d="M60 0 L60 96" />
          <path d="M150 0 L150 96" />
          {/* Diagonals */}
          <path d="M0 90 L240 10" strokeWidth="0.8" opacity="0.5" />
        </g>

        {/* Soft greenery patches */}
        <circle cx="30" cy="20" r="14" fill={isDark ? '#064e3b' : '#bbf7d0'} opacity="0.5" />
        <circle cx="200" cy="60" r="18" fill={isDark ? '#064e3b' : '#bbf7d0'} opacity="0.5" />
        <circle cx="100" cy="80" r="10" fill={isDark ? '#064e3b' : '#bbf7d0'} opacity="0.5" />

        {/* Coast / water hint */}
        <path
          d="M170 96 Q190 78 230 80 L240 96 Z"
          fill={isDark ? '#0c4a6e' : '#bae6fd'}
          opacity="0.7"
        />

        {/* Pins */}
        {[
          { x: 75, y: 28, color: '#6366f1' },
          { x: 130, y: 55, color: '#10b981' },
          { x: 185, y: 30, color: '#f59e0b' },
        ].map((p, i) => (
          <g key={i} transform={`translate(${p.x} ${p.y})`}>
            {/* Pulse ring */}
            <circle cx="0" cy="0" r="9" fill={p.color} opacity="0.18">
              <animate attributeName="r" values="6;11;6" dur="2.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
            </circle>
            {/* Pin body */}
            <path
              d="M0,-9 C-4.5,-9 -7,-5.5 -7,-2 C-7,3 0,9 0,9 C0,9 7,3 7,-2 C7,-5.5 4.5,-9 0,-9 Z"
              fill={p.color}
              stroke="white"
              strokeWidth="1.2"
            />
            <circle cx="0" cy="-3" r="2" fill="white" />
          </g>
        ))}
      </svg>

      {/* Soft footer caption */}
      <div
        className={`absolute bottom-0 inset-x-0 flex items-center justify-between px-2 py-1 text-[9px] sm:text-[10px] font-semibold ${
          isDark ? 'bg-black/40 text-slate-200' : 'bg-white/85 text-slate-600'
        } backdrop-blur-sm`}
      >
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-2.5 h-2.5 text-indigo-500" />
          {localize({ ar: '3 معلمين قريبين', en: '3 tutors nearby' })}
        </span>
        <span className="px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[8px] sm:text-[9px] tracking-wide font-black">
          {localize({ ar: 'قريباً', en: 'SOON' })}
        </span>
      </div>
    </div>
  );
};

export default MiniMapPreview;
